import { arrowRight } from "@simplybusiness/icons";
import { Box, Container, Flex, Image, Text } from "@simplybusiness/mobius";
import { useSnowplowContext } from "@simplybusiness/services";
import classNames from "classnames/dedupe";
import { useCallback } from "react";
import { useProductInfoDrawer } from "../../hooks/useProductInfoDrawer";
import type { ShowcaseProps } from "../../pages/ShowcasePage/types";
import { ProductInfoDrawer } from "../shared/ProductInfoDrawer";
import { useShowcaseReveal } from "./useShowcaseReveal";
import { Disclaimers } from "./Disclaimers";
import "./Showcase.css";
import { ShowcaseExploreOptions } from "./ShowcaseExploreOptions";
import { ShowcaseForm } from "./ShowcaseForm";
import { ShowcasePriceBlock } from "./ShowcasePriceBlock";
import { ShowcaseProductsList } from "./ShowcaseProductsList";

const ACCEPT_SHOWCASE_ACTION = "Accept showcase";
const DECLINE_SHOWCASE_ACTION = "Decline showcase";

export const Showcase = ({
  showcaseName,
  coverageText,
  targetAudienceText,
  showcaseProducts,
  actions,
  offerUuid,
  showcaseAccepted,
  tradeImage,
  recommendations,
  disclaimers,
}: ShowcaseProps) => {
  useShowcaseReveal();

  const { snowplow } = useSnowplowContext();
  const { isOpen, activeProduct, openDrawer, closeDrawer } =
    useProductInfoDrawer();
  const submitAction = actions.find(
    action =>
      action.actionType === "submit" && action.name === ACCEPT_SHOWCASE_ACTION,
  );

  const declineAction = actions.find(
    action =>
      action.actionType === "submit" && action.name === DECLINE_SHOWCASE_ACTION,
  );

  const backAction = actions.find(action => action.actionType === "back");

  const shouldShowExploreOptions = declineAction && recommendations.length > 1;

  const containerClasses = classNames("mobius-showcase__layout", {
    "mobius-showcase__layout--single-recommendation": !shouldShowExploreOptions,
    "mobius-showcase__layout--multiple-recommendations":
      shouldShowExploreOptions,
  });

  const showCaseLayoutTestId = shouldShowExploreOptions
    ? "showcase-layout-multiple-recommendations"
    : "showcase-layout-single-recommendation";

  const handleOpenDrawer = useCallback(
    (productId: string, title: string, content: string) => {
      openDrawer({ title, content });
      snowplow?.trigger("learnMoreModalOpened", { product: productId });
    },
    [openDrawer, snowplow],
  );
  return (
    <Container size="sm">
      <Text
        variant="title"
        elementType="h2"
        className="mobius-showcase-heading"
      >
        Recommended for you
      </Text>
      <Box className={containerClasses} data-testid={showCaseLayoutTestId}>
        <Box className="mobius-showcase__card">
          <Box
            className="mobius-showcase__image-wrapper"
            data-testid="showcase-image-wrapper"
          >
            <Image
              alt={`${showcaseName} trade image`}
              className="mobius-showcase__image"
              src={tradeImage}
              fetchPriority="high"
            />
          </Box>
          <Flex
            className="mobius-showcase__content"
            flexDirection="column"
            gap="md"
          >
            <Box>
              <Text elementType="h2" className="mobius-showcase__title">
                {showcaseName}
              </Text>
              <ShowcasePriceBlock showcaseProducts={showcaseProducts} />
              <Text className="mobius-showcase__coverage">{coverageText}</Text>
              {targetAudienceText && (
                <Text className="mobius-showcase__audience">
                  {targetAudienceText}
                </Text>
              )}
            </Box>
            <Box>
              <p className="mobius-showcase__heading">What&#39;s included:</p>
              <ShowcaseProductsList
                products={showcaseProducts}
                onOpenDrawer={handleOpenDrawer}
              />
            </Box>
            {submitAction && (
              <ShowcaseForm
                action={submitAction}
                className="mobius-showcase-form__accept-showcase-cta"
                icon={arrowRight}
                hiddenInputs={[
                  { name: "showcase_accepted", value: showcaseAccepted },
                  { name: "offer_uuid", value: offerUuid },
                ]}
              />
            )}
          </Flex>
        </Box>
        {shouldShowExploreOptions && (
          <Box
            className="mobius-showcase-explore"
            data-testid="showcase-explore-options"
          >
            <ShowcaseExploreOptions
              recommendations={recommendations}
              declineAction={declineAction}
            />
          </Box>
        )}
      </Box>
      <Disclaimers disclaimers={disclaimers} />
      {backAction && (
        <ShowcaseForm
          action={backAction}
          buttonVariant="secondary"
          className="mobius-showcase-form__back"
          buttonClassName="mobius-showcase-back__button"
        />
      )}
      <ProductInfoDrawer
        isOpen={isOpen}
        onClose={closeDrawer}
        title={activeProduct?.title}
        content={activeProduct?.content}
      />
    </Container>
  );
};
